import { api, type ApiResponse } from '../client';

export interface AdminDashboardSummary {
  totalDonations: number;
  pendingRequests: number;
  registeredUsers: number;
  /** Approved + paid by employer, awaiting L2G to pay the charity */
  awaitingL2gPayout?: number;
  /** Paid to charity, awaiting the charity to confirm receipt */
  awaitingCharityConfirmation?: number;
  pendingPayoutsCount?: number;
  pendingPayoutsValue?: number;
  pendingConfirmationCount?: number;
  pendingConfirmationValue?: number;
  unconfirmedBankDetailsCount?: number;
  /** Charities invited but not yet accepted (outstanding charity invitations) */
  charitiesInvitedCount?: number;
  /** Employers invited but not yet accepted (outstanding employer invitations) */
  employersInvitedCount?: number;
}

export interface RecentPendingPayout {
  employer_payment_uuid: string;
  employer_name: string;
  charity_uuid: string;
  charity_name: string;
  amount: number;
  payment_date: string | null;
}

export interface RecentPendingConfirmationCharity {
  charity_uuid: string;
  charity_name: string;
  payout_count: number;
  total_amount: number;
  latest_paid_at: string | null;
}

export interface RecentUnconfirmedBankDetails {
  charity_uuid: string;
  charity_name: string;
  updated_at: string | null;
}

export interface DonationTrend {
  month: string;
  amount: number;
}

export interface TopCharity {
  uuid: string;
  name: string;
  totalDonation: number;
}

export interface RecentRequest {
  requestId: string;
  employeeName: string;
  leaveHours: number;
  date: string;
  status: string;
  /** Canonical lifecycle stage from the backend (App\Support\DonationLifecycle) */
  stage?: string;
  statusLabel?: string;
  statusClass?: string;
  waitingOn?: string;
  employerName: string;
  employerTopUp: number;
}

export interface AdminDashboardData {
  summary: AdminDashboardSummary;
  donationTrends: DonationTrend[];
  topCharities: TopCharity[];
  recentRequests: RecentRequest[];
  recentPendingPayouts?: RecentPendingPayout[];
  recentPendingConfirmation?: RecentPendingConfirmationCharity[];
  recentUnconfirmedBankDetails?: RecentUnconfirmedBankDetails[];
}

export interface AdminDashboardListFilters {
  page?: number;
  per_page?: number;
  phrase?: string;
}

export interface AdminDashboardListPaging {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface AdminDashboardListResponse<T> {
  success: boolean;
  data: { items: T[]; paging: AdminDashboardListPaging } | null;
  status: number;
  message: string;
}

class AdminDashboardService {
  private baseUrl = '/dashboard/admin';

  /**
   * Get dashboard data for admin user
   */
  async getDashboardData(limit: number = 5): Promise<{ success: boolean; data: AdminDashboardData | null; status: number; message: string }> {
    try {
      const response = await api.get<{ data: AdminDashboardData }>(this.baseUrl, {
        params: { limit }
      });
      
      return {
        success: true,
        data: response.data.data,
        status: response.status,
        message: response.statusText || 'Dashboard data loaded successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message || 'Failed to load dashboard data'
      };
    }
  }

  async getPendingPayouts(
    filters: AdminDashboardListFilters = {}
  ): Promise<AdminDashboardListResponse<RecentPendingPayout>> {
    try {
      const response = await api.get<{
        data: { items: RecentPendingPayout[]; paging: AdminDashboardListPaging };
      }>(`${this.baseUrl}/pending-payouts`, { params: filters });

      return {
        success: true,
        data: response.data.data,
        status: response.status,
        message: response.statusText || 'Pending payouts loaded successfully'
      };
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
      return {
        success: false,
        data: null,
        status: err.response?.status || 500,
        message: err.response?.data?.message || err.message || 'Failed to load pending payouts'
      };
    }
  }

  async getPendingConfirmationCharities(
    filters: AdminDashboardListFilters = {}
  ): Promise<AdminDashboardListResponse<RecentPendingConfirmationCharity>> {
    try {
      const response = await api.get<{
        data: { items: RecentPendingConfirmationCharity[]; paging: AdminDashboardListPaging };
      }>(`${this.baseUrl}/pending-confirmation`, { params: filters });

      return {
        success: true,
        data: response.data.data,
        status: response.status,
        message: response.statusText || 'Pending confirmation charities loaded successfully'
      };
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
      return {
        success: false,
        data: null,
        status: err.response?.status || 500,
        message:
          err.response?.data?.message ||
          err.message ||
          'Failed to load charities awaiting confirmation'
      };
    }
  }

  /**
   * Format currency for display
   */
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Format percentage for display
   */
  static formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  /**
   * Get status text for display
   */
  static getStatusText(status: string): string {
    const statusMap: Record<string, string> = {
      'APPROVED': 'Approved',
      'PENDING': 'Pending',
      'REJECTED': 'Rejected',
      'COMPLETED': 'Completed'
    };
    
    return statusMap[status] || status;
  }

  /**
   * Get status color for display
   */
  static getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'APPROVED': '#10B981',
      'PENDING': '#F59E0B',
      'REJECTED': '#EF4444',
      'COMPLETED': '#10B981'
    };
    
    return colorMap[status] || '#6B7280';
  }
}

export const adminDashboardService = new AdminDashboardService(); 